import { Badge, Button, Grid, Group, NavLink, Paper, ScrollArea, SegmentedControl, Stack, Text, TextInput, Title, Typography } from "@mantine/core";
import { IconDownload, IconPrinter, IconRobot, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { marked } from "marked";
import { useEffect, useMemo, useState } from "react";

import { api, authFetch, unwrap } from "../api/client";
import { useCan } from "../auth/user";
import { toCyrillic, useT } from "../i18n/script";

/**
 * Uputstvo (GT-09) — generise ga backend iz stvarnog stanja sistema (moduli, role, permisije, indeksi,
 * slojevi, API, istorija izmjena) + rucno pisanog teksta (docs/manual). Uvijek je aktuelno.
 */
export default function HelpPage() {
  const { t, script } = useT();
  const can = useCan();
  const isAdmin = can("admin:users") || can("admin:roles");
  const [aud, setAud] = useState<"user" | "admin">("user");
  const [q, setQ] = useState("");
  const manual = useQuery({
    queryKey: ["manual", aud],
    queryFn: async () => unwrap(await api.GET("/api/v1/help/manual", { params: { query: { audience: aud } } })),
  });
  // link na odjeljak (npr. /uputstvo#g45-...) — skrol kad se uputstvo učita
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (id && manual.data) setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 150);
  }, [manual.data]);
  const sections = useMemo(() => {
    const s = manual.data?.sections ?? [];
    if (!q) return s;
    const ql = q.toLowerCase();
    return s.filter((x) => x.title.toLowerCase().includes(ql) || x.markdown.toLowerCase().includes(ql));
  }, [manual.data, q]);

  const html = (md: string) => {
    const h = marked.parse(md, { async: false }) as string;
    return script === "cyr" ? toCyrillicHtml(h) : h;
  };

  async function download() {
    const r = await authFetch(`/api/v1/help/manual.md?audience=${aud}`);
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement("a");
    a.href = url; a.download = `agrisense-uputstvo-${aud}.md`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Stack>
      <Group justify="space-between" className="no-print">
        <div>
          <Title order={3}>{t(manual.data?.title ?? "Uputstvo")}</Title>
          <Group gap={6}>
            <Text size="sm" c="dimmed">{t("Verzija")} {manual.data?.version} · {t("generisano")} {manual.data?.generated_at}</Text>
            <Badge size="xs" variant="light" leftSection={<IconRobot size={10} />}>{t("automatski")}</Badge>
          </Group>
        </div>
        <Group>
          {isAdmin && <SegmentedControl value={aud} onChange={(v) => setAud(v as "user" | "admin")}
            data={[{ value: "user", label: t("Korisničko") }, { value: "admin", label: t("Administratorsko") }]} />}
          <Button variant="default" leftSection={<IconDownload size={16} />} onClick={download}>Markdown</Button>
          <Button leftSection={<IconPrinter size={16} />} onClick={() => window.print()}>{t("Štampa / PDF")}</Button>
        </Group>
      </Group>
      <Grid>
        <Grid.Col span={{ base: 12, md: 3 }} className="no-print">
          <Paper withBorder p="xs" pos="sticky" top={70}>
            <TextInput size="xs" mb="xs" leftSection={<IconSearch size={14} />} placeholder={t("Pretraga uputstva")}
              value={q} onChange={(e) => setQ(e.currentTarget.value)} />
            <ScrollArea.Autosize mah="70vh">
              {sections.map((s) => (
                <NavLink key={s.id} href={`#${s.id}`} label={t(s.title)} py={4}
                  rightSection={s.generated ? <IconRobot size={12} color="#4a9a55" /> : null} />
              ))}
            </ScrollArea.Autosize>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 9 }}>
          <Paper withBorder p="xl" className="manual">
            <div className="print-only">
              <h1>{t(manual.data?.title ?? "")}</h1>
              <p>{t("Verzija")} {manual.data?.version} · {manual.data?.generated_at}</p>
            </div>
            {sections.map((s) => (
              <section key={s.id} id={s.id} style={{ scrollMarginTop: 70 }}>
                <Group gap={6} mt="lg" mb="xs">
                  <Title order={3}>{t(s.title)}</Title>
                  {s.generated && <Badge size="xs" variant="dot" className="no-print">{t("generisano iz sistema")}</Badge>}
                </Group>
                <Typography>
                  <div dangerouslySetInnerHTML={{ __html: html(s.markdown) }} />
                </Typography>
              </section>
            ))}
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

/** Transliteracija samo tekstualnih cvorova (ne dira HTML tagove, kod i linkove). */
function toCyrillicHtml(html: string): string {
  return html.replace(/(<code>[\s\S]*?<\/code>)|(<[^>]+>)|(&#?\w+;)|([^<&]+)/g, (m, code, tag, ent, txt) =>
    code ?? tag ?? ent ?? toCyrillic(txt ?? m));
}
